import React from 'react';
import {
  plansPath,
  snsPath,
  locationsRootPath,
  faqPath,
  loginPath,
  accountPath,
  orderStatusPath,
  productsIframeBasePath,
  homeHubPath,
} from '@constants/paths';
import { HIDE_PRODUCT_CONNECTED_HOME } from '@constants/base';

/* utils */
import { prevented } from '@utils/event';

/* components */
import ChatLink from '@integrations/gladly/Chat/ChatLink';
import MenuTemplate from '../MenuTemplate';

/* Integrations */
import { useIsPasswordlessCheckoutEnabled } from '@scenes/LoginPage/Modal/LoginPasscode/LoginPasscode.hooks';

/* Client Store */
import { MODAL_LOGIN_PASSWORDLESS, useModalStoreActions } from '@store/client/store.modals';

/* Constants */
import { HT_INFO_SITELINKS } from '@constants/hellotech';

/* Types */
type PrimaryMenuProps = {
  user?: object;
  setServiceMenuVisibleState: BaseAnyFunction;
  logout?: BaseAnyFunction;
  hideMembership?: boolean;
  isMobile?: boolean;
};

const PrimaryMenu = ({ user, setServiceMenuVisibleState, hideMembership, logout, isMobile }: PrimaryMenuProps) => {
  const isPasswordlessCheckoutEnabled = useIsPasswordlessCheckoutEnabled();
  const { updateAppLevelModal } = useModalStoreActions();

  const headerItems = [
    {
      text: 'Services',
      onClick: prevented(() => setServiceMenuVisibleState(true)),
    },
    ...(!HIDE_PRODUCT_CONNECTED_HOME
      ? [
          {
            text: 'Products',
            link: productsIframeBasePath(),
          },
        ]
      : []),
    ...(!hideMembership
      ? [
          {
            text: 'Membership',
            link: plansPath(),
          },
        ]
      : []),
    ...(!HIDE_PRODUCT_CONNECTED_HOME
      ? [
          {
            text: 'My Connected Home',
            link: homeHubPath(),
            showBadge: true,
          },
        ]
      : []),
    {
      text: 'Partner with Us',
      link: HT_INFO_SITELINKS.partnerships.url,
      isExternalLink: true,
    },
    {
      text: 'FAQ',
      link: faqPath(),
    },
  ];
  const openLoginModalAction = () => {
    updateAppLevelModal({ name: MODAL_LOGIN_PASSWORDLESS, visible: true });
  };
  const accountItemConfig = isPasswordlessCheckoutEnabled
    ? { onClick: openLoginModalAction, hideIcon: true }
    : { link: user ? accountPath() : loginPath() };

  const secondaryItems = [
    {
      text: 'My Account',
      ...accountItemConfig,
    },
    {
      text: 'Track My Order',
      link: orderStatusPath(),
    },
    {
      text: 'Locations',
      link: locationsRootPath(),
    },
    {
      text: 'Safety & Support',
      link: snsPath(),
    },
    {
      text: 'Blog',
      link: HT_INFO_SITELINKS.blog.url,
      isExternalLink: true,
    },
    {
      Component: ChatLink,
      text: 'Contact Us',
      type: 'chat',
    },
    ...(user && isMobile
      ? [
          {
            text: 'Sign Out',
            onClick: prevented(() => logout!()),
            hideIcon: true,
          },
        ]
      : []),
  ];
  return <MenuTemplate headerItems={headerItems} secondaryItems={secondaryItems} dataTestId="nav-mobile-primary-menu" />;
};

export default React.memo(PrimaryMenu);
